<template>
  <div class="event-card" :class="{ 'dropdown-open': showDropdown }">
    <div class="card-body p-0 position-relative">
      <!-- Cover image -->
      <RouterLink :to="routeTo" class="cover-link" :aria-label="`View ${title} album`">
        <div class="cover-350">
          <div class="cover-blur" :style="coverBgStyle" aria-hidden="true"></div>
          <div class="cover-gradient" aria-hidden="true"></div>
          <img
            class="cover-img"
            :src="photo"
            :alt="title"
            loading="lazy"
            @error="handleImageError"
          />
        </div>
      </RouterLink>

      <!-- Text content -->
      <div class="album-copy">
        <div class="d-flex justify-content-between align-items-start">
          <h5 class="mb-1 flex-grow-1 pe-2">{{ truncate(title) }}</h5>

          <!-- Custom Dropdown -->
          <div class="dropdown-wrapper" ref="dropdownRef">
            <button
              class="album-menu-btn"
              @click.stop="toggleDropdown"
              @keydown.enter.space="toggleDropdown"
              @keydown.esc="closeDropdown"
              :aria-expanded="showDropdown.toString()"
              aria-haspopup="true"
              :aria-label="`Options for ${title}`"
              :aria-controls="`dropdown-${eventId}`"
            >
              <i class="fas fa-ellipsis-v"></i>
            </button>

            <transition name="fade">
              <ul
                v-if="showDropdown"
                :id="`dropdown-${eventId}`"
                class="dropdown-menu-custom"
                role="menu"
                @click.stop
              > 

                    <!-- Only for my albums -->
                    <template v-if="isMine">
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        class="dropdown-menu-custom__item"
                        @click="openViewPerms"
                      >
                        <i class="fas fa-eye"></i> View Permission
                      </li>
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        class="dropdown-menu-custom__item"
                        @click="openUploadPerms"
                      >
                        <i class="fas fa-upload"></i> Upload Permission
                      </li>
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        class="dropdown-menu-custom__item"
                        @click="openShareAlbumFriends"
                      >
                        <i class="fas fa-share"></i> My Friends
                      </li>
                      <hr />
                    </template>

                    <!-- Always visible -->
                    <li 
                      role="menuitem" 
                      tabindex="0"
                      class="dropdown-menu-custom__item"
                      @click="openShareVia"
                    >
                      <i class="far fa-paper-plane"></i> Share via…
                    </li>

              </ul>
            </transition>
          </div>
        </div>

        <div class="album-chips">
          <span class="album-chip">{{ isMine ? 'My Event' : 'Shared' }}</span>
          <span class="album-chip album-chip--muted">Host : {{ host }}</span>
        </div>
 

        <p class="small text-highlight mb-0 text-uppercase">
          <span class="emoji-icon mr-1" aria-hidden="true">📅</span>
          <span>{{ dateRange }}</span>
        </p>
      </div>
    </div>
  </div>

  <!-- ===== Modals (bound with v-model) ===== -->
  <AlbumViewPermissions
    v-model="showViewPerms"
    :event-id="eventId"
    :session-user-id="sessionUserId"
  />

  <UploadPermissionsModal
    v-model="showUploadPerms"
    :event-id="eventId"
    :session-user-id="sessionUserId"
  />

  <ShareAlbumModal
    v-model="showShareAlbum"
    :event-id="eventId"
    :session-user-id="sessionUserId"
    initial-tab="friends"
  />

  <ShareViaModal
    :visible="showShareVia"
    :title="title"
    :image="photo"
    :description="shareDescription"
    :dates="dateRange"
    :host="host"
    :location="shareLocation"
    :address="shareAddress"
    :map-url="shareMapUrl"
    :url="shareUrl"
    @close="showShareVia = false"
  />
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { RouterLink } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { formatEventDateRange } from '@/utils/date'

// Modals
import AlbumViewPermissions from '@/components/common/AlbumViewPermissions.vue'
import UploadPermissionsModal from '@/components/common/UploadPermissionsModal.vue'
import ShareAlbumModal from '@/components/common/ShareAlbumModal.vue'
import ShareViaModal from '@/components/common/ShareViaModal.vue'

const props = defineProps({
  event: { 
    type: Object, 
    required: true,
    default: () => ({})
  },
  type: { 
    type: String, 
    default: "my",
    validator: (value) => ['my', 'shared', 'other'].includes(value)
  },
  sessionUserId: {
    type: String,
    default: ''
  }
})

/* ========= Session ========= */
const session = useSessionStore()
const sessionUserId = computed(() => 
  props.sessionUserId || String(session.userId ?? '')
)

/* ========= Basics ========= */
const isMine = computed(() => (props.type || 'my').toLowerCase() === 'my')

const title = computed(() => 
  props.event.orgname || props.event.eventname || props.event.title || "Untitled Event"
)

const host = computed(() => 
  props.event.hostname || props.event.host_name || props.event.host || "Unknown"
)

const photo = computed(() => {
  const e = props.event || {}
  const p = e.photo || e.eventphoto || e.cover_image || null
  return p && p !== "0" && p !== "null" && p !== "undefined"
    ? p
    : "https://dummyimage.com/1200x800/cccccc/ffffff&text=No+Image"
})
const coverBgStyle = computed(() => ({ backgroundImage: `url(${photo.value})` }))

const eventId = computed(() =>
  String(props.event?.resolved_id ?? props.event?.id ?? props.event?.eventid ?? '')
)

const routeTo = computed(() => {
  const id = eventId.value
  if (!id) return '/albums'
  
  const source = encodeURIComponent(props.type || "my")
  return `/album/${id}?source=${source}`
})

function truncate(str, maxLength = 60) {
  if (!str || typeof str !== 'string') return ""
  return str.length > maxLength ? str.slice(0, maxLength) + "…" : str
}

// Handle broken images
const handleImageError = (event) => {
  const target = event.target
  target.src = "https://dummyimage.com/1200x800/cccccc/ffffff&text=No+Image"
  target.alt = "Image not available"
}

/* ========= Share helpers ========= */
const dateRange = computed(() => {
  const e = props.event || {}
  const range = formatEventDateRange({
    fromDate: e.fromdate,
    toDate: e.todate,
    fromTime: e.fromtime,
    toTime: e.totime
  })
  return range || 'Date not specified'
})

const shareDescription = computed(() => {
  const e = props.event || {}
  return e.description || e.short_desc || `Check out ${title.value}`
})

const shareLocation = computed(() => props.event?.event_place || '')
const shareAddress = computed(() => {
  const e = props.event || {}
  const parts = [e.event_place, e.door_no, e.address].filter(Boolean)
  return parts.join(', ')
})
const shareMapUrl = computed(() => {
  const e = props.event || {}
  if (e.map_url) return e.map_url
  if (e.lat && e.lng) return `https://www.google.com/maps?q=${encodeURIComponent(e.lat)},${encodeURIComponent(e.lng)}`
  if (shareAddress.value) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shareAddress.value)}`
  return ''
})

const shareUrl = computed(() => {
  try {
    const origin = window?.location?.origin || ''
    return origin + routeTo.value
  } catch {
    return routeTo.value
  }
})

/* ========= Dropdown ========= */
const showDropdown = ref(false)
const dropdownRef = ref(null)
const albumDropdownBus = typeof window !== 'undefined' ? window : null
const closeAllHandler = () => { showDropdown.value = false }

function toggleDropdown() {
  if (!showDropdown.value && albumDropdownBus) {
    albumDropdownBus.dispatchEvent(new CustomEvent('album-dropdown-close-all'))
  }
  showDropdown.value = !showDropdown.value 
}

function closeDropdown() {
  showDropdown.value = false
}

function handleClickOutside(event) {
  if (showDropdown.value && dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

/* ========= Modal state + actions ========= */
const showViewPerms = ref(false)
const showUploadPerms = ref(false)
const showShareAlbum = ref(false)
const showShareVia = ref(false)

// Fixed modal opening functions
function openViewPerms() {
  if (!isMine.value) return
  console.log('Opening View Permissions modal') // Debug log
  showDropdown.value = false
  // Use setTimeout to ensure dropdown closes before modal opens
  setTimeout(() => {
    showViewPerms.value = true
  }, 10)
}

function openUploadPerms() {
  if (!isMine.value) return
  console.log('Opening Upload Permissions modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showUploadPerms.value = true
  }, 10)
}

function openShareAlbumFriends() {
  if (!isMine.value) return
  console.log('Opening Share Album modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showShareAlbum.value = true
  }, 10)
}

function openShareVia() {
  console.log('Opening Share Via modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showShareVia.value = true
  }, 10)
}

// Keyboard navigation for dropdown
function handleKeydown(event) {
  if (!showDropdown.value) return
  
  const menuItems = dropdownRef.value?.querySelectorAll('[role="menuitem"]')
  if (!menuItems?.length) return
  
  let currentIndex = -1
  for (let i = 0; i < menuItems.length; i++) {
    if (menuItems[i] === document.activeElement) {
      currentIndex = i
      break
    }
  }
  
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    const nextIndex = (currentIndex + 1) % menuItems.length
    menuItems[nextIndex]?.focus()
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    const prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length
    menuItems[prevIndex]?.focus()
  } else if (event.key === 'Escape') {
    showDropdown.value = false
    dropdownRef.value?.querySelector('button')?.focus()
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
  document.addEventListener("keydown", handleKeydown)
  if (albumDropdownBus) {
    albumDropdownBus.addEventListener('album-dropdown-close-all', closeAllHandler)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
  document.removeEventListener("keydown", handleKeydown)
  if (albumDropdownBus) {
    albumDropdownBus.removeEventListener('album-dropdown-close-all', closeAllHandler)
  }
})
</script>

<style scoped>
.event-card {
  position: relative;
  border-radius: 22px;
  padding: 1rem;
  background: linear-gradient(180deg, #fff8fb 0%, #fff2f7 100%);
  border: 1px solid #f2d5df;
  box-shadow: 0 10px 24px rgba(242, 172, 196, 0.14);
  margin-bottom: 1.1rem;
  overflow: visible;
  transition: transform .18s ease, box-shadow .18s ease;
}
.event-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(242, 172, 196, 0.2);
}

.card-body h5 {
  font-size: 1.08rem;
  line-height: 1.3;
  color: #2b1b22;
}
.text-highlight{
  color:#7a4a5f;
  font-weight:700;
}
.emoji-icon{
  display:inline-block;
  line-height:1;
}

.album-copy{
  padding-top: 14px;
}

.album-chips{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin: 4px 0 8px;
}

.album-chip{
  display:inline-flex;
  align-items:center;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#ec4899;
  border-radius:999px;
  padding:4px 10px;
  font-size:.78rem;
  font-weight:700;
}

.album-chip--muted{
  color:#9f6480;
}

/* fixed-height cover */
.cover-350 { 
  height: 350px; 
  overflow: hidden; 
  border-radius: 18px;
  position: relative;
  background: linear-gradient(180deg, #fff6fa 0%, #ffeaf3 100%);
  border: 1px solid #f4cfdb;
}
.cover-blur{
  position:absolute;
  inset:0;
  background-size:cover;
  background-position:center;
  filter:blur(28px) saturate(1.1);
  transform:scale(1.08);
  opacity:0;
  transition:opacity .2s ease;
  border-radius:inherit;
}
.cover-gradient{
  position:absolute;
  inset:0;
  background:linear-gradient(135deg, rgba(58, 19, 36, .42), rgba(58, 19, 36, .08));
  opacity:0;
  transition:opacity .2s ease;
  z-index:0;
  border-radius:inherit;
}
.cover-img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
  position:relative;
  z-index:1;
  border-radius:0.75rem;
}
.cover-link { 
  display: block; 
  text-decoration: none; 
  color: inherit; 
}

/* Hover animation */
/* --- Custom dropdown --- */
.dropdown-wrapper { 
  position: relative; 
  z-index: 3;
}
.album-menu-btn{
  height:34px;
  width:34px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border:1px solid #f1d7e1;
  background:#fff;
  border-radius:999px;
  box-shadow:0 6px 16px rgba(236,72,153,.08);
  color:#654654;
  cursor:pointer;
}
.album-menu-btn:hover {
  background: #fff4f8;
}

.dropdown-menu-custom {
  position: absolute; 
  right: 0; 
  top: 100%;
  min-width: 220px;
  background: #fff; 
  border-radius: 14px;
  padding: 6px 0; 
  list-style: none; 
  z-index: 1010; 
  border: 1px solid #f3d4df;
  box-shadow: 0 14px 30px rgba(86, 42, 62, 0.16);
}
.event-card.dropdown-open {
  z-index: 4000;
}
.event-card.dropdown-open .dropdown-menu-custom {
  z-index: 4100;
}

.dropdown-menu-custom li {
  padding: 8px 14px; 
  font-size: 0.9rem; 
  cursor: pointer;
  display: flex; 
  align-items: center; 
  gap: 10px; 
  transition: background 0.2s;
  outline: none;
  color:#3b2230;
}
.dropdown-menu-custom__item i{
  width:16px;
  text-align:center;
  color:#ec4899;
}
.dropdown-menu-custom li:hover,
.dropdown-menu-custom li:focus { 
  background: #fff4f8; 
}

/* Divider */
.dropdown-menu-custom hr { 
  margin: 4px 0; 
  border-top: 1px solid #f3d4df; 
}

/* Fade animation */
.fade-enter-active, .fade-leave-active { 
  transition: opacity 0.15s ease, transform 0.15s ease; 
}
.fade-enter-from { 
  opacity: 0;
  transform: translateY(-8px);
}
.fade-leave-to { 
  opacity: 0;
  transform: translateY(-8px);
}

/* Focus styles for accessibility */
.dropdown-wrapper button:focus-visible,
.dropdown-menu-custom li:focus-visible {
  outline: 2px solid #ec4899;
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .cover-350 {
    height: 320px;
  }
  .cover-blur,
  .cover-gradient{
    opacity:1;
  }
  .cover-img{
    object-fit:contain;
    padding:14px;
  }
}
@media (max-width: 768px) {
  .cover-350 {
    height: 260px;
  }
  .cover-img{
    padding:12px;
  }
  .dropdown-menu-custom {
    min-width: 200px;
    right: -8px;
  }
}
@media (max-width: 576px) {
  .cover-350 {
    height: 220px;
  }
  .cover-img{
    padding:10px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
.event-card,
.fade-enter-active,
.fade-leave-active {
  transition: none;
}
}

</style>
